/**
 * /app/$tenantSlug/hiring/requisitions/$id/report — K1.7 per-requisition
 * drill-down report ("download this job's full report").
 *
 * GET /hiring/reports/requisitions/{id} is HR-admin OR the requisition's
 * hiring manager (server-enforced; anyone else gets a neutral 404 the UI
 * renders as "not available"). The applicant roster includes candidate
 * name/email — exactly the audience that already sees them on the
 * pipeline/candidate surfaces.
 */

import { useMemo } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Download, GitBranch } from "lucide-react";
import {
  useRequisitionReport,
} from "@/hooks/use-hiring";
import { toApiError } from "@/lib/api";
import { toCsv } from "@/lib/csv";
import {
  APPLICATION_STATUS_LABELS,
  CANDIDATE_SOURCE_LABELS,
  OFFER_STATUS_LABELS,
  applicationTone,
  fmtDate,
} from "@/lib/hiring-format";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import {
  PHONE_BACK_LINK_CLASS,
  PhoneBackRow,
} from "@/components/hiring/phone-back-row";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  CandidateSource,
  HiringRequisitionReport,
} from "@/types/hiring";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/requisitions/$id/report",
)({
  component: RequisitionReportRoute,
});

function fmtDaysShort(d: number | null | undefined): string {
  if (d == null || Number.isNaN(d)) return "—";
  return `${d.toFixed(1)}d`;
}

function sourceLabel(s: string): string {
  return (
    CANDIDATE_SOURCE_LABELS[s as CandidateSource] ??
    s.charAt(0).toUpperCase() + s.slice(1)
  );
}

function ReportView({ id }: Readonly<{ id: number }>) {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const q = useRequisitionReport(id);

  const report = q.data;
  const funnelSorted = useMemo(
    () =>
      [...(report?.funnel ?? [])].sort(
        (a, b) => a.sort_order - b.sort_order,
      ),
    [report?.funnel],
  );

  if (q.isLoading) {
    return (
      <HiringModuleShell title="Requisition Report">
        <Card>
          <CardContent className="space-y-4 py-8">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }
  if (q.isError || !report) {
    const e = q.isError ? toApiError(q.error) : null;
    return (
      <HiringModuleShell title="Requisition Report">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {!e || e.error === "not_found"
                ? "This report isn't available. You need to be an HR admin or this requisition's hiring manager."
                : e.message}
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link
                to="/app/$tenantSlug/hiring/requisitions"
                params={{ tenantSlug }}
              >
                <ArrowLeft className="h-4 w-4" />
                All Requisitions
              </Link>
            </Button>
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  return (
    <>
    <PhoneBackRow>
      <Link
        to="/app/$tenantSlug/hiring/requisitions"
        params={{ tenantSlug }}
        className={PHONE_BACK_LINK_CLASS}
      >
        <ArrowLeft className="h-4 w-4" />
        All Requisitions
      </Link>
    </PhoneBackRow>
    <HiringModuleShell
      title={`Report — ${report.requisition.title}`}
      description={
        [
          report.requisition.department,
          `open ${report.summary.days_open} days`,
        ]
          .filter(Boolean)
          .join(" · ")
      }
      actions={
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => exportCsv(report)}
          >
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/hiring/requisitions/$id/pipeline"
              params={{ tenantSlug, id: String(id) }}
            >
              <GitBranch className="h-4 w-4" />
              Pipeline
            </Link>
          </Button>
          <Button asChild variant="ghost" className="hidden gap-1.5 lg:inline-flex">
            <Link
              to="/app/$tenantSlug/hiring/requisitions"
              params={{ tenantSlug }}
            >
              <ArrowLeft className="h-4 w-4" />
              All Requisitions
            </Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: "Applied", value: String(report.summary.applied) },
            {
              label: "In Pipeline",
              value: String(report.summary.in_pipeline),
            },
            { label: "Hired", value: String(report.summary.hired) },
            { label: "Rejected", value: String(report.summary.rejected) },
            {
              label: "Withdrawn",
              value: String(report.summary.withdrawn),
            },
            {
              label: "Avg Time-to-Hire",
              value:
                report.summary.avg_time_to_hire_days == null
                  ? "—"
                  : `${report.summary.avg_time_to_hire_days.toFixed(1)}d`,
            },
          ].map((s) => (
            <Card key={s.label}>
              <CardHeader className="pb-2">
                <CardDescription>{s.label}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Applicants</CardTitle>
            <CardDescription>
              Every application on this requisition, newest first.
              Days-in-stage comes from the stage-event history.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {report.applicants.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No applications yet.
              </p>
            ) : (
              <>
              {/* Phones: a card per applicant (the 7-column table cut the
                  stage off at 308 px). */}
              <ul className="-mx-2 divide-y sm:hidden">
                {report.applicants.map((a) => (
                  <li key={a.application_id} className="space-y-1.5 px-2 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Link
                          to="/app/$tenantSlug/hiring/candidates/$id"
                          params={{
                            tenantSlug,
                            id: String(a.candidate_id),
                          }}
                          search={{ application: a.application_id }}
                          className="inline-flex min-h-11 items-center break-words font-medium text-primary hover:underline"
                        >
                          {a.name || a.email}
                        </Link>
                        <p className="truncate text-xs text-muted-foreground" title={a.email}>
                          {a.email}
                        </p>
                      </div>
                      <StatusBadge
                        label={APPLICATION_STATUS_LABELS[a.status]}
                        tone={applicationTone(a.status)}
                        className="mt-3 shrink-0"
                      />
                    </div>
                    <p className="text-sm">
                      <span className="font-medium">{a.current_stage}</span>
                      {a.days_in_current_stage != null && (
                        <span className="text-muted-foreground">
                          {" "}
                          · {fmtDaysShort(a.days_in_current_stage)} in stage
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {[
                        sourceLabel(a.source),
                        `Applied ${fmtDate(a.applied_at)}`,
                        a.offer_status
                          ? `Offer: ${OFFER_STATUS_LABELS[a.offer_status]}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </li>
                ))}
              </ul>
              <Table className="hidden sm:table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">
                      In Stage
                    </TableHead>
                    <TableHead>Applied</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Offer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.applicants.map((a) => (
                    <TableRow key={a.application_id}>
                      <TableCell>
                        <Link
                          to="/app/$tenantSlug/hiring/candidates/$id"
                          params={{
                            tenantSlug,
                            id: String(a.candidate_id),
                          }}
                          search={{ application: a.application_id }}
                          className="inline-flex items-center font-medium text-primary hover:underline pointer-coarse:min-h-11"
                        >
                          {a.name || a.email}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {a.email}
                        </p>
                      </TableCell>
                      <TableCell>{sourceLabel(a.source)}</TableCell>
                      <TableCell>{a.current_stage}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtDaysShort(a.days_in_current_stage)}
                      </TableCell>
                      <TableCell>{fmtDate(a.applied_at)}</TableCell>
                      <TableCell>
                        <StatusBadge
                          label={APPLICATION_STATUS_LABELS[a.status]}
                          tone={applicationTone(a.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {a.offer_status
                          ? OFFER_STATUS_LABELS[a.offer_status]
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">By Source</CardTitle>
            </CardHeader>
            <CardContent>
              {report.by_source.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No applications yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Applied</TableHead>
                      <TableHead className="text-right">Hired</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_source.map((r) => (
                      <TableRow key={r.source}>
                        <TableCell>{sourceLabel(r.source)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.applied}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.hired}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Funnel (Entered Stage)
              </CardTitle>
              <CardDescription>
                Distinct applications entering each stage, lifetime of
                this requisition (from the stage-event epoch).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Entered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {funnelSorted.map((r) => (
                    <TableRow key={r.stage_id}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.entered}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </HiringModuleShell>
    </>
  );
}

function exportCsv(report: HiringRequisitionReport) {
  const today = new Date().toISOString().slice(0, 10);
  const lines: string[][] = [];
  lines.push([
    "Requisition report",
    report.requisition.title,
    today,
  ]);
  lines.push([]);
  lines.push(["Summary"]);
  lines.push(["Applied", String(report.summary.applied)]);
  lines.push(["In pipeline", String(report.summary.in_pipeline)]);
  lines.push(["Hired", String(report.summary.hired)]);
  lines.push(["Rejected", String(report.summary.rejected)]);
  lines.push(["Withdrawn", String(report.summary.withdrawn)]);
  lines.push([
    "Avg time-to-hire (days)",
    report.summary.avg_time_to_hire_days == null
      ? "—"
      : report.summary.avg_time_to_hire_days.toFixed(1),
  ]);
  lines.push(["Days open", String(report.summary.days_open)]);
  lines.push([]);
  lines.push([
    "Candidate",
    "Email",
    "Source",
    "Current stage",
    "Days in stage",
    "Applied",
    "Outcome",
    "Offer",
  ]);
  for (const a of report.applicants) {
    lines.push([
      a.name,
      a.email,
      sourceLabel(a.source),
      a.current_stage,
      a.days_in_current_stage == null
        ? ""
        : a.days_in_current_stage.toFixed(1),
      a.applied_at.slice(0, 10),
      APPLICATION_STATUS_LABELS[a.status],
      a.offer_status ? OFFER_STATUS_LABELS[a.offer_status] : "",
    ]);
  }
  lines.push([]);
  lines.push(["Funnel stage", "Entered"]);
  for (const f of [...report.funnel].sort(
    (a, b) => a.sort_order - b.sort_order,
  )) {
    lines.push([f.name, String(f.entered)]);
  }
  const blob = new Blob([toCsv(lines)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `requisition-report-${report.requisition.id}-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function RequisitionReportRoute() {
  const { id } = Route.useParams();
  const numId = Number(id);
  return (
    <HiringFeatureGate>
      {Number.isFinite(numId) && numId > 0 ? (
        <ReportView id={numId} />
      ) : (
        <HiringModuleShell title="Requisition Report">
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-sm text-muted-foreground">
                Invalid requisition.
              </p>
            </CardContent>
          </Card>
        </HiringModuleShell>
      )}
    </HiringFeatureGate>
  );
}
